import Vue from "vue";

export const CenterState = {
  currentCenter: {
    id: 0,
    name: "",
    currency: "", // IDR | USD
    currency_sym: "", // Rp | $
    date_format: "", // DD/MM/YYYY
    logo_url: "",
    timezone: "",
    status: "",
    contact_name: "",
    contact_email: "",
    contact_phone_number: "",
    center_address: "",
    team_size_limit: null,

    enable_appointment: false,
    enable_sales_pipeline: false,
    enable_branch: false,
    enable_analytics: false,

    deactivated_at: "",
    deleted_at: "",
    total_active_members: 0,
    total_active_admins: 0,
    total_active_therapists: 0,
    total_targets: 0,
    total_curricula: 0,

    parent_form: {},
    is_template: false,
    template_center_id: 0,

    currency_settings: {
      id: "",
      alternate_symbols: [],
      decimal_mark: ",",
      disambiguate_symbol: null,
      html_entity: "",
      iso_code: "",
      iso_numeric: "",
      name: "",
      priority: 0,
      smallest_denomination: 0,
      subunit: "",
      subunit_to_unit: 0,
      symbol: "",
      symbol_first: false,
      thousands_separator: "", // . | ,
      format: ""
    }
  },
  center: null,
  centers: [],
  totalCenters: 0
};

export const CenterGetters = {
  currentCenter: state => {
    return state.currentCenter;
  },
  center: state => {
    return state.center;
  },
  centers: state => {
    return state.centers;
  },
  totalCenters: state => {
    return state.totalCenters;
  }
};

export const CenterMutations = {
  setCurrentCenter: (state, data) => {
    if (data) {
      const index = state.centers.findIndex(
        i => Number(i.id) === Number(data.id)
      );
      if (index > -1) {
        const arr = [...state.centers];
        arr[index] = data;
        Vue.set(state, "centers", arr);
      }
      if (state.center && Number(data.id) === Number(state.center.id)) {
        Vue.set(state, "center", data);
      }
    }
    Vue.set(state, "currentCenter", data);
  },
  setCenter: (state, data) => {
    if (data) {
      const index = state.centers.findIndex(
        i => Number(i.id) === Number(data.id)
      );
      if (index > -1) {
        const arr = [...state.centers];
        arr[index] = data;
        Vue.set(state, "centers", arr);
      }
      if (Number(data.id) === Number(state.currentCenter.id)) {
        Vue.set(state, "currentCenter", data);
      }
    }
    Vue.set(state, "center", data);
  },
  setCenters: (state, data) => {
    Vue.set(state, "centers", data.centers);
    Vue.set(state, "totalCenters", data.total_count);
  },
  addCenter: (state, data) => {
    Vue.set(state, "centers", [data, ...state.centers]);
    Vue.set(state, "totalCenters", state.totalCenters + 1);
  },
  removeCenter: (state, data) => {
    const arr = state.centers.filter(i => Number(i.id) !== Number(data.id));
    Vue.set(state, "centers", arr);
    Vue.set(state, "totalCenters", state.totalCenters - 1);
  }
};

export const CenterActions = {
  getCurrentCenter: ({ commit, dispatch }) => {
    return Vue.axios.secured
      .get(`/api/v1/center`)
      .then(response => {
        commit("setCurrentCenter", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch current center from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateCurrentCenter: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/center`, data)
      .then(response => {
        commit("setCurrentCenter", response.data);
        const title = data.title || "Settings updated";
        const message =
          data.message || "Success! The information has been updated.";
        dispatch("showToast", { title, message, type: "success" });
        dispatch("checkCurrentUser");
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update current center.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  //
  getCenters: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    return Vue.axios.secured
      .get(`/api/v1/centers${params}`)
      .then(response => {
        commit("setCenters", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch centers from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  getCenter: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .get(`/api/v1/centers/${data.id}`)
      .then(response => {
        commit("setCenter", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch center from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  createCenter: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post("/api/v1/centers", { center: data.center })
      .then(response => {
        commit("addCenter", response.data);
        const title = "New center created";
        const message = "Success! A new center has been created.";
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to create center.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateCenter: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/centers/${data.id}`, { center: data.center })
      .then(response => {
        commit("setCenter", response.data);
        const title = data.title || "Center updated";
        const message = data.message || "Success! The center has been updated.";
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update center.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  deleteCenter: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .delete(`/api/v1/centers/${data.id}`)
      .then(response => {
        commit("removeCenter", data);
        const title = "Center deleted";
        const message = `Success! ${data.center_name} has been deleted.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to delete center.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },

  centerDatabankCopy: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/centers/${data.id}/copy_databank`, {
        databank_ids: data.databank_ids
      })
      .then(response => {
        const title = "Databank copied";
        const message = "Success! The databank has been copied.";
        commit("setCenter", response.data);
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to copy databank.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },

  centerResetDatabank: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .delete(`/api/v1/centers/${data.id}/reset_databank`)
      .then(response => {
        const title = "Databank reset";
        const message = "Success! The databank has been reset.";
        commit("setCenter", response.data);
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to reset databank.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/center.store.js